import React, { useState } from "react";
import { MapPin, CalendarDays, Sprout, Loader2, PlusCircle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PhotoUploader } from "./PhotoUploader";
import { CodePlate, StatusStamp, Barcode, EntryCard, fmtDate } from "./BloomBits";

const UpdateForm = ({ code, onAdded }) => {
  const [notes, setNotes] = useState("");
  const [flowerColour, setFlowerColour] = useState("");
  const [growthHabit, setGrowthHabit] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/bloom/${code}/entries`, {
        notes, photos, flower_colour: flowerColour, growth_habit: growthHabit,
      });
      toast.success("Update logged in the passport");
      setNotes(""); setFlowerColour(""); setGrowthHabit(""); setPhotos([]);
      onAdded(data.entry);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log the update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-cream border-[3px] border-dashed border-forest/40 rounded-[2rem] p-6" data-testid="bloom-update-form">
      <h4 className="font-serif-display uppercase text-plum-deep text-lg tracking-[0.1em] flex items-center gap-2">
        <PlusCircle className="w-5 h-5 text-forest" /> LOG A NEW UPDATE
      </h4>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        placeholder="How's it growing? New buds, first flower, colour changes…"
        data-testid="bloom-update-notes"
        className="mt-4 w-full rounded-3xl px-5 py-3 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-forest text-sm resize-none"
      />
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <input value={flowerColour} onChange={(e) => setFlowerColour(e.target.value)} placeholder="Flower colour (optional)" data-testid="bloom-update-colour" className="rounded-full px-5 py-2.5 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-forest text-sm" />
        <input value={growthHabit} onChange={(e) => setGrowthHabit(e.target.value)} placeholder="Growth habit (optional)" data-testid="bloom-update-habit" className="rounded-full px-5 py-2.5 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-forest text-sm" />
      </div>
      <div className="mt-4">
        <PhotoUploader photos={photos} onChange={setPhotos} />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="bloom-update-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-7 py-3.5 font-serif-display uppercase text-xs tracking-[0.18em] font-semibold hover:bg-plum transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sprout className="w-4 h-4" />}
        LOG UPDATE
      </button>
    </form>
  );
};

export const Passport = ({ record, entries, onEntryAdded }) => (
  <div className="space-y-8" data-testid="bloom-passport">
    <div className="relative bg-plum text-cream rounded-[2rem] p-6 md:p-10 overflow-hidden">
      <Barcode className="absolute top-0 left-8 right-8 text-cream/15" />
      <div className="relative flex flex-wrap items-start gap-5 pt-4">
        <div className="flex-1 min-w-[220px]">
          <p className="font-serif-display uppercase tracking-[0.3em] text-lemon text-[11px] font-semibold">THE BLOOM FOUNDRY · OFFICIAL PLANT PASSPORT</p>
          <h2 className="font-serif-display uppercase text-3xl md:text-4xl mt-2 leading-tight" data-testid="bloom-passport-name">
            {record.plant_name || "UNIDENTIFIED BLOOM"}
          </h2>
          <div className="mt-3"><CodePlate code={record.code} /></div>
        </div>
        <StatusStamp status={record.status} />
      </div>
      {record.identification && (
        <div className="relative mt-6 bg-forest/25 border-2 border-forest rounded-2xl p-4 flex items-start gap-3" data-testid="bloom-identification">
          <BadgeCheck className="w-6 h-6 text-lemon shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-lemon font-bold">TEAM IDENTIFICATION</p>
            <p className="mt-1 text-sm text-cream/95">{record.identification}</p>
          </div>
        </div>
      )}
      <div className="relative mt-6 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.14em] text-cream/70">
        {record.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{record.location}</span>}
        {record.planting_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />PLANTED {record.planting_date}</span>}
        <span className="inline-flex items-center gap-1.5"><Sprout className="w-3.5 h-3.5" />FIRST LOGGED {fmtDate(record.created_at)}</span>
      </div>
    </div>

    <UpdateForm code={record.code} onAdded={onEntryAdded} />

    <div>
      <h3 className="font-serif-display uppercase text-plum-deep text-xl tracking-[0.1em]">GROWING HISTORY ({entries.length})</h3>
      <div className="mt-4 space-y-4 relative pl-6">
        <span className="absolute left-1.5 top-2 bottom-2 border-l-2 border-dashed border-magenta/40" aria-hidden />
        {entries.map((e) => (
          <div key={e.id} className="relative">
            <span className={`absolute -left-6 top-5 w-3.5 h-3.5 rounded-full border-2 border-cream ${e.author === "team" ? "bg-forest" : "bg-magenta"}`} aria-hidden />
            <EntryCard entry={e} />
          </div>
        ))}
      </div>
    </div>
  </div>
);
